/**
 * Step 2: the plan. A title, and the headings the body will be written to.
 *
 * Between the brief and the article, because both of the things it decides are
 * cheap to change here and expensive to change later: rewriting a 5,000-character
 * body because the angle was wrong costs a generation, and rewriting a heading
 * costs a click.
 *
 * Nothing here is saved until 保存 - a proposal is a suggestion, and a screen
 * that silently adopted the first title it was handed would be choosing for the
 * writer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLoaderData, useLocation, useNavigate } from 'react-router';
import {
  ApiError,
  TITLE_AXES,
  patch,
  post,
  put,
  type ArticleDetail,
  type GenerationContext,
  type ProposalResult,
} from '../api.ts';
import { Card, Empty, Pill } from '../components/ui.tsx';
import {
  PLACEMENT,
  REPEATABLE,
  SECTION_KINDS,
  type SectionKind,
} from '../../../shared/section-kinds.ts';
import { StageBar } from '../components/StageBar.tsx';
import { ARTICLE_ANGLES, type ArticleAngle } from '../../../shared/angles.ts';
import { EditPanel, Field } from '../components/EditPanel.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { Textarea } from '../components/Textarea.tsx';
import {
  MaterialPicker,
  materialFrom,
  materialPayload,
  type MaterialOptions,
  type MaterialSelection,
} from '../components/MaterialPicker.tsx';

interface LoaderData extends MaterialOptions {
  article: ArticleDetail;
  context: GenerationContext;
}

/**
 * The heading each shape arrives with.
 *
 * A default, not a rule - every one is editable in the row below. The summary
 * names the subject, because 「ダーマペンが30秒でわかる」 is the line that makes a
 * reader stay, and 「まとめ」 is the line that makes them scroll past.
 */
const SHAPE_HEADINGS: Record<SectionKind, (context: GenerationContext) => string> = {
  body: () => '',
  summary: (context) => `${context.article.keywords[0] ?? 'この施術'}が30秒でわかる`,
  checklist: () => 'こんな方におすすめ',
  steps: () => '施術の流れ',
  faq: () => 'よくある質問',
  reasons: (context) => `${context.clinic.name}が選ばれる理由`,
};

/** Why each shape is there, in the words the drafting prompt is given. */
const SHAPE_INTENTS: Record<SectionKind, string> = {
  body: '',
  summary: '読者が最初の10秒で知りたいことに答えるため',
  checklist: '読者が自分に当てはまるかを判断できるようにするため',
  steps: '当日の流れを具体的に想像できるようにするため',
  faq: '検索意図の顕在・潜在ニーズに短く答えるため',
  reasons: '他院ではなくこの院を選ぶ理由を、登録済みの強みで示すため',
};

/** One planned section, as this screen holds it before saving. */
interface PlannedSection {
  heading: string;
  intent: string;
  /** The shape it is written in. See shared/section-kinds.ts. */
  kind: SectionKind;
  /** The approved facts this section should build on. A focus, not a filter. */
  mustInclude: Array<{ kind: string; id: string }>;
}

export default function ArticlePlan() {
  const loaded = useLoaderData<LoaderData>();
  const { article, context } = loaded;
  const navigate = useNavigate();
  const location = useLocation();

  const [proposals, setProposals] = useState<ProposalResult | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * The choice, seeded from whatever the article already carries.
   *
   * Sections rather than strings: the reason a heading was proposed is part of
   * the outline, and it used to be dropped the moment somebody pressed save -
   * so the body was written from ten bare headings and read like it. An article
   * planned before that lands here with its headings and empty reasons.
   */
  const [title, setTitle] = useState(article.title);
  const [chosen, setChosen] = useState<PlannedSection[]>(() =>
    article.sections.length > 0
      ? article.sections.map((section) => ({
          heading: section.heading,
          intent: section.intent,
          kind: section.kind,
          mustInclude: section.must_include ?? [],
        }))
      : article.headings.map((heading) => ({
          heading,
          intent: '',
          kind: 'body' as const,
          mustInclude: [],
        })),
  );
  /** The shape of the article, which decides what headings are worth proposing. */
  const [angle, setAngle] = useState<ArticleAngle | ''>(article.angle ?? '');
  /**
   * The named alternative for a comparison table/section.
   *
   * Loaded facts, not free text: a comparison table naming a treatment nobody
   * registered would have nothing true to put in its column. Cleared whenever
   * 比較 stops being the angle, so an article that changes its mind does not
   * quietly keep asking every section to compare against something it no
   * longer says it is about.
   */
  const [comparisonTreatmentId, setComparisonTreatmentId] = useState(
    article.comparison_treatment_id ?? '',
  );
  /** Which section's fact picker is open, by position. */
  const [openFacts, setOpenFacts] = useState<number | null>(null);
  /** Just the headings, for the "already chosen" checks the proposal list makes. */
  const headings = chosen.map((section) => section.heading);

  /*
   * Every approved fact this article may be written from, once.
   *
   * Built here rather than inside the row that renders the picker: it is the
   * same list for every section - what differs is which of them that section
   * claimed - and rebuilding it per row made the list an accident of where it
   * was read. It is also what the unassigned check below counts against.
   *
   * Already narrowed by the article's own material scope: `context` comes back
   * from the server with `strength_scope` applied, so a category the writer set
   * to 選ぶ never offers what it excluded.
   */
  const facts = useMemo(
    () =>
      [
        ...(context.treatment?.name
          ? [
              {
                kind: 'treatments',
                id: article.treatment_id ?? '',
                label: `施術: ${context.treatment.name}`,
              },
            ]
          : []),
        ...Object.values(context.strengths)
          .flat()
          .map((row) => ({ kind: 'strengths', id: row.id, label: `強み: ${row.title}` })),
        ...context.supervisors.map((row) => ({
          kind: 'supervisors',
          id: row.id,
          label: `監修医: ${row.name}`,
        })),
        ...(context.cta?.name && article.cta_template_id
          ? [
              {
                kind: 'cta-templates',
                id: article.cta_template_id,
                label: `CTA: ${context.cta.name}`,
              },
            ]
          : []),
      ].filter((fact) => fact.id !== ''),
    [context, article.treatment_id, article.cta_template_id],
  );

  const factLabel = (kind: string, id: string) =>
    facts.find((fact) => fact.kind === kind && fact.id === id)?.label ?? null;

  /*
   * Facts no section has claimed.
   *
   * Only worth saying once at least one section HAS claimed something: on a
   * fresh outline every fact is unassigned and that is the normal state, not a
   * warning. Once assignments exist, the leftovers are the ones the body will
   * only mention in passing - which is the thing a writer wants to know before
   * pressing 本文を作成, not after reading the draft.
   */
  const assigned = new Set(
    chosen.flatMap((section) => section.mustInclude.map((fact) => `${fact.kind}-${fact.id}`)),
  );
  const unassigned = assigned.size > 0
    ? facts.filter((fact) => !assigned.has(`${fact.kind}-${fact.id}`))
    : [];

  const [editingBrief, setEditingBrief] = useState(false);
  const [keywords, setKeywords] = useState<string[]>(article.keywords);
  const [material, setMaterial] = useState<MaterialSelection>(materialFrom(article));

  /**
   * Ask for proposals.
   *
   * Two behaviours, because they answer two different wishes and the writer
   * knows which one they are having:
   *
   *   replace - these are not what I wanted. The old options go.
   *   append  - these are fine, I want more to choose from. The old options stay
   *             and the model is told what they were, so it varies the angle
   *             instead of rephrasing them.
   *
   * `only` narrows either one to titles or headings, so a good set of headings
   * is never thrown away to get another go at the titles.
   */
  async function propose(options: { only?: 'titles' | 'headings'; append?: boolean } = {}) {
    const { only, append = false } = options;
    setBusy(`${append ? 'add' : 'redo'}-${only ?? 'all'}`);
    setError(null);
    try {
      // Only what this round could duplicate: asking it to avoid ten headings
      // while it writes titles spends context on nothing.
      const existing = proposals
        ? [
            ...(only === 'headings' ? [] : proposals.titles.map((t) => t.text)),
            ...(only === 'titles' ? [] : proposals.headings.map((h) => h.text)),
          ]
        : [];
      const result = await post<ProposalResult>(`/articles/${article.id}/proposals`, {
        ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        ...(only ? { only } : {}),
        ...(append && existing.length > 0 ? { avoid: existing.slice(0, 40) } : {}),
      });
      setProposals((current) => {
        if (!current) return result;
        // Dedupe on merge as well as in the prompt: the instruction is a request,
        // and the same title arriving twice must not appear twice on screen.
        const merge = <T extends { text: string }>(kept: T[], incoming: T[]) => [
          ...kept,
          ...incoming.filter((entry) => !kept.some((k) => k.text === entry.text)),
        ];
        const titles =
          only === 'headings'
            ? current.titles
            : append
              ? merge(current.titles, result.titles)
              : result.titles;
        const headings =
          only === 'titles'
            ? current.headings
            : append
              ? merge(current.headings, result.headings)
              : result.headings;
        /*
         * The intent survives a top-up round.
         *
         * A round asking only for more headings returns no analysis - it was not
         * asked for one - and replacing the block on screen with null would
         * delete the reading the writer just approved.
         */
        return { ...result, titles, headings, intent: result.intent ?? current.intent };
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === 'prompt_not_configured'
            ? 'この生成に使う指示文がまだ保存されていません。JMCにプロンプトの設定を依頼してください。'
            : err.status === 409
            ? '提案に必要な情報が不足しています。下の「記事の条件」でペルソナを選んでください。'
            : err.code === 'quota_exceeded'
              ? '今月の生成上限に達しています。'
              : err.code === 'rate_limited'
                ? 'AIサービスが混み合っています。しばらく待って再試行してください。'
                : (err.fields[0] ?? err.message)
          : '提案を生成できませんでした',
      );
    } finally {
      setBusy(null);
    }
  }

  /*
   * Arriving from the wizard runs one round immediately - that is what pressing
   * 進む meant. A reload does not: the flag lives in the navigation state, so
   * refreshing this screen never quietly spends a second generation.
   */
  const started = useRef(false);
  useEffect(() => {
    const wanted = (location.state as { propose?: boolean } | null)?.propose === true;
    if (!wanted || started.current) return;
    started.current = true;
    void propose({});
    // Deliberately once, on mount: `propose` spends a generation, so re-running
    // it whenever a dependency changed would be money.
  }, []);

  /**
   * Take a proposal, or put it back.
   *
   * Adding carries the reason across: that line is what the section is FOR, and
   * it travels with the heading into the generation.
   */
  const toggleHeading = (heading: string, intent = '') =>
    setChosen((current) =>
      current.some((entry) => entry.heading === heading)
        ? current.filter((entry) => entry.heading !== heading)
        : [...current, { heading, intent, kind: 'body', mustInclude: [] }],
    );

  /**
   * Point a section at one approved fact, or stop pointing at it.
   *
   * A focus, not a filter: the article may still state anything approved. What
   * this decides is whose job a fact is - which is why the price used to appear
   * in four sections and the equipment in none.
   */
  const toggleFact = (at: number, kind: string, id: string) =>
    setChosen((current) =>
      current.map((entry, index) => {
        if (index !== at) return entry;
        const has = entry.mustInclude.some((fact) => fact.kind === kind && fact.id === id);
        return {
          ...entry,
          mustInclude: has
            ? entry.mustInclude.filter((fact) => !(fact.kind === kind && fact.id === id))
            : [...entry.mustInclude, { kind, id }],
        };
      }),
    );

  /**
   * Add a section of a given shape.
   *
   * A shape rather than a heading somebody types, because each of these is
   * WRITTEN differently and looks different on the page: the summary is a table
   * the reader scans in ten seconds, the checklist is something they tick
   * against themselves, the flow is numbered, the FAQ is questions. Those are
   * the parts that make a published clinic column readable, and asking one
   * prompt for all of them is what produced fifteen identical blocks of prose.
   *
   * Placement is part of the shape: a summary that is not the first thing on the
   * page is not a summary, and a FAQ after the closing invitation is read by
   * nobody. See PLACEMENT in shared/section-kinds.ts.
   */
  const addShaped = (kind: SectionKind) =>
    setChosen((current) => {
      if (!REPEATABLE.includes(kind) && current.some((entry) => entry.kind === kind)) {
        return current;
      }
      const section: PlannedSection = {
        heading: SHAPE_HEADINGS[kind](context),
        intent: SHAPE_INTENTS[kind],
        kind,
        mustInclude: [],
      };
      if (PLACEMENT[kind] === 'start') return [section, ...current];
      if (PLACEMENT[kind] === 'here') return [...current, section];

      const last = current.at(-1);
      // A closing 「ご案内」 section stays last.
      const closing = last && /案内|相談|カウンセリング|まとめ/.test(last.heading);
      return closing ? [...current.slice(0, -1), section, last] : [...current, section];
    });

  /** Edit a chosen heading in place, keeping its position and its reason. */
  const editHeading = (at: number, next: string) =>
    setChosen((current) =>
      current.map((entry, index) => (index === at ? { ...entry, heading: next } : entry)),
    );

  /** Edit why the section exists. The writer's words outrank the model's. */
  const editIntent = (at: number, next: string) =>
    setChosen((current) =>
      current.map((entry, index) => (index === at ? { ...entry, intent: next } : entry)),
    );

  const move = (at: number, by: -1 | 1) =>
    setChosen((current) => {
      const next = [...current];
      const target = at + by;
      if (target < 0 || target >= next.length) return current;
      [next[at], next[target]] = [next[target]!, next[at]!];
      return next;
    });

  async function save(then?: 'write') {
    setSaving(true);
    setError(null);
    try {
      /*
       * Two writes, in this order, because they are two different things.
       *
       * The article carries the title, the keywords and the material; the
       * outline is its own resource and replaces the whole list. The sections
       * endpoint also refreshes `articles.headings`, so the version moves once
       * more - which is why the screen reloads afterwards rather than reusing
       * the version it holds.
       */
      await patch(`/articles/${article.id}`, {
        version: article.version,
        title,
        keywords,
        // '' is "no particular shape", which is a real answer and clears it.
        angle: angle === '' ? null : angle,
        comparisonTreatmentId: comparisonTreatmentId === '' ? null : comparisonTreatmentId,
        ...materialPayload(material),
      });
      await put(`/articles/${article.id}/sections`, {
        sections: chosen.map((section) => ({
          heading: section.heading,
          intent: section.intent,
          kind: section.kind,
          mustInclude: section.mustInclude,
        })),
      });
      if (then === 'write') {
        void navigate(`/articles/${article.id}/write`, { state: { write: true } });
      } else {
        // Reload so `version` advances; the next save would otherwise be stale.
        void navigate(`/articles/${article.id}/plan`, { replace: true });
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'この記事は別の場所で更新されました。再読み込みしてください。'
            : (err.fields[0] ?? err.message)
          : '保存できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Every step, in both directions: going back to a finished step is a
          normal thing to want, and nothing is lost by it. */}
      <StageBar articleId={article.id} current={'plan'} />
      <Card
        title="記事の切り口"
        caption={`${article.store_name}・第${article.version}版・切り口で見出しの構成が変わります`}
        actions={
          <Link className="btn btn--ghost btn--sm" to="/articles">
            一覧に戻る
          </Link>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        {/*
          The angle, before anything is proposed.

          The first decision a human editor makes and the one this product never
          asked for: every article was built from the same brief with a different
          keyword, so every article came out the same shape. This changes which
          headings are worth proposing, which is the difference between two
          articles and one article written twice.
        */}
        <div className="chips">
          <button
            type="button"
            className="chip"
            aria-pressed={angle === ''}
            onClick={() => setAngle('')}
          >
            指定なし
          </button>
          {(Object.keys(ARTICLE_ANGLES) as ArticleAngle[]).map((key) => (
            <button
              key={key}
              type="button"
              className="chip"
              aria-pressed={angle === key}
              title={ARTICLE_ANGLES[key].hint}
              onClick={() => setAngle(key)}
            >
              {ARTICLE_ANGLES[key].label}
            </button>
          ))}
        </div>
        {angle !== '' && <p className="muted small">{ARTICLE_ANGLES[angle].brief}</p>}
        {/* Air between the chips and the field below them. */}
        <div style={{ height: 10 }} />

        {/*
          Only when the angle is a comparison: a name here is a promise that a
          comparison table can be written truthfully, which needs a SECOND
          treatment's approved facts loaded - not just a name typed in.
        */}
        {angle === 'compare' && (
          <div className="field" style={{ maxWidth: 360 }}>
            <label htmlFor="compare-treatment">比較する相手の施術</label>
            <select
              id="compare-treatment"
              className="input"
              value={comparisonTreatmentId}
              onChange={(e) => setComparisonTreatmentId(e.target.value)}
            >
              <option value="">選択しない（一般的な比較として書く）</option>
              {loaded.treatments.items
                .filter((entry) => entry.id !== material.treatmentId)
                .map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {typeof entry.name === 'string' ? entry.name : '（名称なし）'}
                  </option>
                ))}
            </select>
            <span className="field__hint">
              選ぶと、この施術の承認済み情報も記事に渡され、比較の節で実名での比較表が書けます。
              選ばないと、一般的な特徴の比較になります。
            </span>
          </div>
        )}

        <div className="field">
          <label htmlFor="plan-instruction">生成の指示</label>
          <input
            id="plan-instruction"
            maxLength={500}
            placeholder="例：疑問形のタイトルにしたい／ダウンタイムを前半で説明したい"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
          />
          <span className="field__hint">
            この指示は提案の作り直しにだけ使われ、記事には保存されません。
          </span>
        </div>

      </Card>

      {/*
        The title and the headings, in one card because they are one decision
        made twice: the proposals for both come from the same call, and choosing
        a title changes which headings make sense under it.

        Separated from the card above because that one is INPUT - the angle and
        the instruction that shape a proposal - and this one is the OUTPUT the
        writer chooses from. Reading them as one long column was the clutter.
      */}
      <Card
        title="タイトルと見出し"
        caption="提案は候補です。選んだあと、そのまま書き換えられます。"
        actions={
          <button
            className="btn btn--sm"
            type="button"
            disabled={busy !== null}
            onClick={() => void propose({})}
          >
            {busy === 'redo-all' ? '生成中…' : proposals ? 'すべて作り直す' : '提案を生成'}
          </button>
        }
      >
        <div className="field">
          <label htmlFor="plan-title">
            この記事のタイトル<span className="req">必須</span>
          </label>
          <input
            id="plan-title"
            required
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {proposals === null && busy === null ? (
          <p className="muted small">
            「提案を生成」で、承認済みの情報からタイトル案と見出し案を作成します。
          </p>
        ) : proposals === null ? (
          /*
           * The shape of what is coming, rather than a word on a button. The
           * reader's eye stays where the answer will appear.
           */
          <>
            <p className="form__section">タイトル案</p>
            <div className="proposalskeleton">
              <div className="proposalskeleton__row" />
              <div className="proposalskeleton__row" />
              <div className="proposalskeleton__row" />
            </div>
          </>
        ) : (
          <>
            {/*
              STEP 1, before the choices it explains.

              The three paragraphs are why the titles below are worth choosing
              between: a title is an angle on a need, and the writer cannot judge
              the angle without seeing the need written down.
            */}
            {proposals.intent && (
              <>
                <p className="form__section">検索意図</p>
                <dl className="deflist" style={{ marginBottom: 14 }}>
                  <div>
                    <dt className="muted small">検索背景</dt>
                    <dd>{proposals.intent.background || <span className="muted">—</span>}</dd>
                  </div>
                  <div>
                    <dt className="muted small">顕在ニーズ</dt>
                    <dd>{proposals.intent.explicit || <span className="muted">—</span>}</dd>
                  </div>
                  <div>
                    <dt className="muted small">潜在ニーズ</dt>
                    <dd>{proposals.intent.latent || <span className="muted">—</span>}</dd>
                  </div>
                </dl>
                <p className="muted small" style={{ marginTop: -8 }}>
                  この分析は保存され、本文生成でも同じ意図に沿って書かれます。
                </p>
              </>
            )}

            <p className="form__section">
              タイトル案
              {(busy === 'redo-titles' || busy === 'add-titles') && (
                <span className="muted small">{'　'}作り直しています…</span>
              )}
            </p>
            <div className="chips" style={{ marginBottom: 6 }}>
              <button
                className="chip"
                type="button"
                disabled={busy !== null}
                onClick={() => void propose({ only: 'titles', append: true })}
              >
                {busy === 'add-titles' ? '生成中…' : 'タイトル案を追加'}
              </button>
              <button
                className="chip"
                type="button"
                disabled={busy !== null}
                onClick={() => void propose({ only: 'titles' })}
              >
                {busy === 'redo-titles' ? '生成中…' : 'タイトル案を作り直す'}
              </button>
            </div>
            {proposals.titles.length === 0 ? (
              <Empty>タイトル案が返りませんでした。指示を変えて再生成してください。</Empty>
            ) : (
              <div
                className={`proposallist${
                  busy === 'redo-titles' || busy === 'add-titles' ? ' proposallist--busy' : ''
                }`}
              >
                {proposals.titles.map((option) => (
                  <button
                    key={option.text}
                    type="button"
                    className="pickcard"
                    aria-pressed={title === option.text}
                    onClick={() => setTitle(option.text)}
                  >
                    <span>
                      {/* The angle, not decoration: three titles that read
                          similarly are still three different bets on what the
                          reader came for. */}
                      <span className="pill" style={{ marginBottom: 4, display: 'inline-block' }}>
                        {TITLE_AXES[option.axis]}
                      </span>
                      <span className="pickcard__name" style={{ display: 'block' }}>
                        {option.text}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            <p className="form__section">
              見出し案
              {(busy === 'redo-headings' || busy === 'add-headings') && (
                <span className="muted small">{'　'}作り直しています…</span>
              )}
            </p>
            <div className="chips" style={{ marginBottom: 6 }}>
              <button
                className="chip"
                type="button"
                disabled={busy !== null}
                onClick={() => void propose({ only: 'headings', append: true })}
              >
                {busy === 'add-headings' ? '生成中…' : '見出し案を追加'}
              </button>
              <button
                className="chip"
                type="button"
                disabled={busy !== null}
                onClick={() => void propose({ only: 'headings' })}
              >
                {busy === 'redo-headings' ? '生成中…' : '見出し案を作り直す'}
              </button>
              <button
                className="chip"
                type="button"
                disabled={busy !== null || proposals.headings.length === 0}
                onClick={() =>
                  setChosen((current) => [
                    ...current,
                    // Each proposal keeps the reason it was proposed with.
                    ...proposals.headings
                      .filter((entry) => !current.some((c) => c.heading === entry.text))
                      .map((entry) => ({
                        heading: entry.text,
                        intent: entry.reason,
                        kind: 'body' as const,
                        mustInclude: [],
                      })),
                  ])
                }
              >
                すべて採用
              </button>
            </div>
            <div
              className={`proposallist${
                busy === 'redo-headings' || busy === 'add-headings' ? ' proposallist--busy' : ''
              }`}
            >
              {proposals.headings.map((heading) => (
                <button
                  key={heading.text}
                  type="button"
                  className="pickcard"
                  aria-pressed={headings.includes(heading.text)}
                  onClick={() => toggleHeading(heading.text)}
                >
                  <span>
                    <span className="pickcard__name" style={{ display: 'block' }}>
                      {heading.text}
                    </span>
                    {/* Why this section earns its place. Without it a writer is
                        picking ten headings on the sound of them. */}
                    {heading.reason && (
                      <span className="muted small">{heading.reason}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        <p className="form__section">
          採用した見出し（{headings.length}）
          <span className="muted small">{'　'}本文はこの順に書かれます</span>
        </p>
        {headings.length === 0 ? (
          <Empty>見出しが未選択です。提案から選ぶか、下の「見出しを追加」で書けます。</Empty>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {chosen.map((section, at) => (
              <div key={`${section.heading}-${at}`}>
              <div className="headingrow">
                <input
                  aria-label={`見出し ${at + 1}`}
                  maxLength={120}
                  value={section.heading}
                  onChange={(e) => editHeading(at, e.target.value)}
                />
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  aria-label={`${section.heading} を上へ`}
                  disabled={at === 0}
                  onClick={() => move(at, -1)}
                >
                  ↑
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  aria-label={`${section.heading} を下へ`}
                  disabled={at === headings.length - 1}
                  onClick={() => move(at, 1)}
                >
                  ↓
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  aria-label={`${section.heading} を削除`}
                  onClick={() => setChosen(chosen.filter((_, index) => index !== at))}
                >
                  ×
                </button>
              </div>
              {/*
                Which approved facts are this section's job.

                A focus, not a filter - the article may still state anything
                approved - and it is the fix for the price appearing in four
                sections while the equipment appeared in none.
              */}
              <div className="factrow">
                <button
                  type="button"
                  className="chip"
                  aria-pressed={openFacts === at}
                  aria-expanded={openFacts === at}
                  onClick={() => setOpenFacts(openFacts === at ? null : at)}
                >
                  使う情報
                  {section.mustInclude.length > 0 ? ` (${section.mustInclude.length})` : ''}
                </button>
                {/* What this section IS, where its shape is not ordinary prose. */}
                {section.kind !== 'body' && (
                  <Pill tone="ok">{SECTION_KINDS[section.kind].label}</Pill>
                )}
                {/*
                  What it was pointed at, while the picker is shut. A count told
                  the writer that a decision existed without telling them what it
                  was, so checking one section's facts meant opening every
                  section - and the whole reason to assign facts is to see the
                  spread across the outline at a glance.
                */}
                {openFacts !== at &&
                  section.mustInclude.map((fact) => {
                    const label = factLabel(fact.kind, fact.id);
                    return label === null ? null : (
                      <span key={`${fact.kind}-${fact.id}`} className="tag">
                        {label}
                      </span>
                    );
                  })}
              </div>

              {openFacts === at && (
                <div className="factpick">
                  {/*
                    Chosen first, then the rest - dimmed, full size, still one
                    press away. Every approved fact stays offered for every
                    section: a fact belonging to one section is a decision about
                    emphasis, not a lock, and the writer is the one who knows
                    that the 監修医 belongs in the safety section as well.
                  */}
                  <div className="chips">
                    {[...facts]
                      .sort((left, right) => {
                        const on = (fact: { kind: string; id: string }) =>
                          section.mustInclude.some(
                            (entry) => entry.kind === fact.kind && entry.id === fact.id,
                          )
                            ? 0
                            : 1;
                        return on(left) - on(right);
                      })
                      .map((fact) => {
                        const on = section.mustInclude.some(
                          (entry) => entry.kind === fact.kind && entry.id === fact.id,
                        );
                        return (
                          <button
                            key={`${fact.kind}-${fact.id}`}
                            type="button"
                            className={`chip${on ? '' : ' chip--dim'}`}
                            aria-pressed={on}
                            onClick={() => toggleFact(at, fact.kind, fact.id)}
                          >
                            {fact.label}
                          </button>
                        );
                      })}
                  </div>
                  <p className="factpick__label" style={{ margin: 0 }}>
                    選んだ情報はこの節の中心になり、用途まで書かれます。選ばなかった情報は、
                    担当の節がある場合はこの節では扱いません。
                  </p>
                </div>
              )}

              {/*
                Why this section exists, editable.

                Written by the model when the heading was proposed, and it is now
                SENT WITH THE HEADING when the body is generated - a section told
                what need it answers is written to that need. The writer's wording
                outranks the model's, which is why this is a box and not a label.
              */}
              <input
                className="headingrow__intent"
                aria-label={`見出し ${at + 1} の狙い`}
                maxLength={200}
                value={section.intent}
                placeholder="この見出しの狙い（例：ダウンタイム中の不安に答えるため）"
                onChange={(e) => editIntent(at, e.target.value)}
              />
              </div>
            ))}
          </div>
        )}
        {/*
          The leftovers, once anybody has started assigning.

          Not an error - an article does not have to give every approved fact a
          home. It is the answer to 「なぜこの強みが本文に出てこないのか」 given
          before the draft exists rather than after reading it: anything here is
          available to the whole article but is nobody's job, so the writing only
          reaches for it when the section genuinely needs it.
        */}
        {unassigned.length > 0 && (
          <p className="muted small" style={{ margin: '10px 0 0' }}>
            どの節にも割り当てていない情報（本文全体で必要なときだけ使われます）：
            <span className="taglist" style={{ display: 'inline-flex', marginLeft: 6 }}>
              {unassigned.map((fact) => (
                <span key={`${fact.kind}-${fact.id}`} className="tag">
                  {fact.label}
                </span>
              ))}
            </span>
          </p>
        )}
        <div className="editpanel__actions">
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() =>
              setChosen([...chosen, { heading: '', intent: '', kind: 'body', mustInclude: [] }])
            }
          >
            自分で見出しを追加
          </button>
          {/*
            The parts that make a clinic column readable rather than long: the
            opening early-read table, the checklist a reader ticks against
            themselves, the numbered flow, why-this-clinic, and the questions.
            Each is written to its own shape by the drafting prompt - see
            shared/section-kinds.ts.
          */}
          {(['summary', 'checklist', 'steps', 'reasons', 'faq'] as const).map((kind) => (
            <button
              key={kind}
              className="btn btn--ghost btn--sm"
              type="button"
              title={SECTION_KINDS[kind].hint}
              disabled={
                !REPEATABLE.includes(kind) && chosen.some((entry) => entry.kind === kind)
              }
              onClick={() => addShaped(kind)}
            >
              {SECTION_KINDS[kind].label}を追加
            </button>
          ))}
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            disabled={busy !== null}
            onClick={() => void propose({ only: 'headings', append: true })}
          >
            {busy === 'add-headings' ? '生成中…' : 'AIで見出し案を追加'}
          </button>
        </div>
      </Card>

      {/*
        The brief, editable here. Sending somebody back to another screen to
        change the persona and then forward again is how a two-step flow becomes
        a four-step one.
      */}
      <Card
        title="記事の条件"
        caption="この内容から提案と本文が作られます。"
        actions={
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => setEditingBrief((open) => !open)}
          >
            {editingBrief ? '閉じる' : '編集'}
          </button>
        }
      >
        {editingBrief ? (
          <EditPanel
            open
            onClose={() => setEditingBrief(false)}
            submitLabel="条件を保存"
            onSubmit={async () => {
              await save();
              setEditingBrief(false);
            }}
          >
            <Field
              label="キーワード"
              htmlFor="plan-keywords"
              span="full"
              hint="1つ目が狙うキーワードです。ほかのタグを押すと入れ替わります。"
            >
              <KeywordInput id="plan-keywords" value={keywords} onChange={setKeywords} primary />
            </Field>
            <MaterialPicker
              idPrefix="plan"
              value={material}
              onChange={setMaterial}
              options={loaded}
            />
            <Field label="編集方針" htmlFor="plan-note" span="full" hint="保存済みの指示です。">
              <Textarea id="plan-note" minRows={2} value={context.article.generationNote} readOnly />
            </Field>
          </EditPanel>
        ) : (
          <dl className="deflist">
            {/*
              The keywords across the whole row, as tags.

              Joined with 「・」 they were a paragraph: a clinic writing long-tail
              Japanese registers a dozen of them, so one cell of the grid grew to
              twenty lines while the five cells beside it stayed one line each -
              a card that was 80% empty and still hard to read. Tags are counted
              at a glance, and the first one is marked because it is the keyword
              the article is written to rank for.
            */}
            <div className="deflist__wide">
              <dt className="muted small">対策キーワード（{keywords.length}）</dt>
              <dd>
                {keywords.length === 0 ? (
                  <span className="muted">未登録</span>
                ) : (
                  <span className="taglist">
                    {keywords.map((word, at) => (
                      <span key={word} className={`tag${at === 0 ? ' tag--primary' : ''}`}>
                        {word}
                      </span>
                    ))}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="muted small">ペルソナ</dt>
              <dd>{context.persona?.label ?? <span className="muted">未選択</span>}</dd>
            </div>
            <div>
              <dt className="muted small">施術</dt>
              <dd>{context.treatment?.name ?? <span className="muted">未選択</span>}</dd>
            </div>
            <div>
              <dt className="muted small">CTA</dt>
              <dd>{context.cta?.name ?? <span className="muted">未選択</span>}</dd>
            </div>
            <div>
              <dt className="muted small">文体</dt>
              <dd>{context.resolved.tone}</dd>
            </div>
            <div>
              <dt className="muted small">店舗</dt>
              <dd>{context.store.name}</dd>
            </div>
          </dl>
        )}
      </Card>

      {/*
        Everything the writing step will be given, on the screen where it is
        decided.

        The structure IS settled here - the next screen writes to it and the
        editor only shows it - but until this panel existed a writer had to take
        that on trust: they pressed 保存して本文を作成 and found out afterwards which
        persona, which treatment and which length had been used. Showing the
        assembled brief is the difference between confirming a plan and hoping.
      */}
      <Card
        title="この内容で本文を作成します"
        caption="下の情報だけを使って、決めた見出しの順に書かれます。"
      >
        <dl className="deflist">
          <div>
            <dt className="muted small">タイトル</dt>
            <dd>{title || <span className="muted">未設定</span>}</dd>
          </div>
          <div>
            <dt className="muted small">店舗</dt>
            <dd>{context.store.name}</dd>
          </div>
          <div>
            <dt className="muted small">狙うキーワード</dt>
            <dd>{keywords[0] ?? <span className="muted">未設定</span>}</dd>
          </div>
          <div className="deflist__wide">
            <dt className="muted small">本文で使うキーワード</dt>
            <dd>
              {/* The same list, including the primary one: it is also a keyword
                  the article must mention, and excluding it here would be a
                  second rule to remember. */}
              {(() => {
                const all = [...new Set([...keywords, ...context.resolved.keywords])];
                return all.length === 0 ? (
                  <span className="muted">なし</span>
                ) : (
                  <span className="taglist">
                    {all.map((word) => (
                      <span key={word} className={`tag${word === keywords[0] ? ' tag--primary' : ''}`}>
                        {word}
                      </span>
                    ))}
                  </span>
                );
              })()}
            </dd>
          </div>
          <div>
            <dt className="muted small">ペルソナ</dt>
            <dd>
              {context.persona?.label ?? <span className="muted">未選択</span>}
              {context.persona?.concerns && (
                <span className="muted small" style={{ display: 'block' }}>
                  {context.persona.concerns}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="muted small">施術</dt>
            <dd>{context.treatment?.name ?? <span className="muted">未選択</span>}</dd>
          </div>
          <div>
            <dt className="muted small">文体</dt>
            <dd>{article.tone || context.resolved.tone}</dd>
          </div>
          <div>
            <dt className="muted small">目安文字数</dt>
            <dd>
              {article.target_length
                ? `${article.target_length.toLocaleString('ja-JP')}文字`
                : '4,000〜5,000文字（既定）'}
            </dd>
          </div>
        </dl>

        {/*
          The outline as it will be written: the order, the purpose of each
          section, and - once saved - how long each one is aiming at. This is
          the contract the writing step follows.
        */}
        <p className="form__section">構成（この順に書かれます）</p>
        {chosen.length === 0 ? (
          <Empty>見出しが未選択です。上の「見出し案」から選ぶか、自分で追加してください。</Empty>
        ) : (
          <ol className="outline">
            {chosen.map((section, at) => (
              <li key={`${section.heading}-${at}`} className="outline__row">
                <span className="outline__jump" style={{ cursor: 'default' }}>
                  <span className="outline__heading">
                    {section.heading || <span className="muted">（見出し未入力）</span>}
                  </span>
                  <span className="muted small">
                    {section.intent || '狙い未設定'}
                    {/*
                      The per-section length, once the outline has been saved -
                      the article's target divided across it. Absent before the
                      first save, because there is nothing to divide yet.
                    */}
                    {article.sections[at]?.target_chars ? (
                      <> ・ 目安 {article.sections[at]?.target_chars?.toLocaleString('ja-JP')}文字</>
                    ) : null}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        {context.missing.length > 0 && (
          <p className="muted small" style={{ marginTop: 0 }}>
            不足：{context.missing.join('、')}
          </p>
        )}
        <div className="editpanel__actions">
          <button
            className="btn"
            type="button"
            disabled={saving || title.trim() === '' || headings.length === 0}
            onClick={() => void save('write')}
          >
            {saving ? '保存中…' : 'この構成で本文を作成'}
          </button>
          <button
            className="btn btn--ghost"
            type="button"
            disabled={saving}
            onClick={() => void save()}
          >
            保存だけする
          </button>
          {proposals && (
            <Pill tone="muted">
              {proposals.provider === 'stub' ? 'スタブ生成（課金なし）' : proposals.model}
            </Pill>
          )}
        </div>
      </Card>
    </>
  );
}
